// control-center/src/lib/giteaBotCredentials.ts
// Typed API client for the /api/control-center/gitea-bot-credentials endpoints.

/** Throws with the HTTP status + any parseable server-side detail message. */
async function ok<T>(r: Response, fn: string): Promise<T> {
  if (r.ok) return r.json() as Promise<T>;
  let detail = '';
  try {
    const body = await r.json() as Record<string, unknown>;
    if (body.error) detail = ` — ${body.error}`;
    if (body.message) detail += `: ${body.message}`;
  } catch {
    try {
      const text = await r.text();
      if (text) detail = ` — ${text.slice(0, 200)}`;
    } catch { /* nothing */ }
  }
  throw new Error(`${fn} ${r.status}${detail}`);
}

export interface GiteaBotRow {
  id: string;
  bot_name: string;
  consumed_by_product: string;
  scopes: string[];
  current_pat_name: string | null;
  last_minted_at: string | null;
  drift_detected_at: string | null;
  drift_reason: string | null;
  status: 'active' | 'archived';
}

export interface MintResult {
  vault_path: string;
  pat_name: string;
  masked_value: string;
  _value?: string;
}

const BASE = '/api/control-center/gitea-bot-credentials';

export async function listBots(): Promise<{ bots: GiteaBotRow[] }> {
  const r = await fetch(BASE, { credentials: 'include' });
  return ok<{ bots: GiteaBotRow[] }>(r, 'listBots');
}

export async function mintForBot(bot: string): Promise<MintResult> {
  const r = await fetch(`${BASE}/${encodeURIComponent(bot)}/mint`, {
    method: 'POST',
    credentials: 'include',
  });
  return ok<MintResult>(r, 'mint');
}

export async function createBot(body: {
  bot_name: string;
  product: string;
  ssh_pubkey?: string;
}): Promise<MintResult> {
  const r = await fetch(BASE, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return ok<MintResult>(r, 'create');
}

export async function revokePat(bot: string, name: string): Promise<void> {
  const r = await fetch(
    `${BASE}/${encodeURIComponent(bot)}/tokens/${encodeURIComponent(name)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  await ok<void>(r, 'revoke');
}

export async function revealPat(bot: string): Promise<MintResult> {
  const r = await fetch(`${BASE}/${encodeURIComponent(bot)}/reveal`, {
    credentials: 'include',
  });
  return ok<MintResult>(r, 'reveal');
}
